import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, Spinner, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import PropertySelectComponent from './payment/PropertySelectComponent';
import RoomSelectComponent from './payment/RoomSelectComponent';
import DateRangePickerComponent from './payment/DateRangePickerComponentV2';
import PaymentTableReport from './payment/PaymentTableReport';
import SkeletonTable from '../components/sales/SkeletonTable';

// 1. Impor skema filter yang lama DAN skema respons yang baru
import { FilterSchema, FilterSchemaType } from '../../../services/payment-report/schema/BookingSchema';
import { useQueryClient } from '@tanstack/react-query';
// GANTI DENGAN PATH YANG BENAR KE SKEMA BARU ANDA
import usePaymentReport from '../../../services/payment/hooks/usePaymentReport';

const PaymentReportComponent: React.FC = () => {
    const queryClient = useQueryClient();

    const { control, handleSubmit, formState: { errors }, setValue, watch } = useForm<FilterSchemaType>({
        resolver: zodResolver(FilterSchema),
        defaultValues: {
            filter: {
                room_type_id: "",
                periode: {
                    start: "",
                    end: "",
                }
            },
        },
    });

    const [activeFilter, setActiveFilter] = useState<FilterSchemaType["filter"] | null>(null);

    const {
        data: reportData,
        isFetching,
        isError,
        error
    } = usePaymentReport({
        filter: activeFilter,
    });

    const currentFormValues = watch();

    const isFormChanged = activeFilter
        ? currentFormValues.filter?.property_id !== (activeFilter.property_id || "") ||
          currentFormValues.filter?.room_type_id !== (activeFilter.room_type_id || "") ||
          currentFormValues.filter?.periode?.start !== activeFilter.periode?.start ||
          currentFormValues.filter?.periode?.end !== activeFilter.periode?.end
        : false;

    const showRefresh = !isFetching && reportData && activeFilter !== null && !isFormChanged;

    // 2. MAPPING: Mengubah data dari format API ke format yang dibutuhkan oleh tabel.
    // `useMemo` memastikan proses mapping hanya berjalan saat `reportData` berubah.
    const mappedData = useMemo(() => {
        if (!reportData?.data) return [];

        return reportData.data.map(item => ({
            // Kiri: Key yang dibutuhkan Tabel (sesuai dummy data) | Kanan: Key dari API
            date: item.date_payment,
            added: item.created_at || item.date_payment, // Jika created_at null, gunakan date_payment
            room_type: item.room_type_name,
            room_number: item.room_number,
            user: item.user_name,
            source: item.channex_ota_name || "Direct", // Jika OTA null, anggap "Direct"
            reservation_number: item.invoice_number,
            check_in: item.checkin_date,
            guest_name: `${item.customer_name} ${item.customer_surname}`, // Gabungkan nama
            card_type: item.payment_type,
            surcharge: Number(item.surcharge), // Konversi string ke angka
            amount: Number(item.total_amount), // Gunakan total_amount dan konversi ke angka
        }));
    }, [reportData]); // Dependensi: hanya kalkulasi ulang jika reportData berubah

    // 3. KALKULASI TOTAL: Karena API tidak menyediakan total, kita hitung di frontend.
    // `useMemo` juga digunakan di sini untuk efisiensi.
    const totals = useMemo(() => {
        if (!mappedData || mappedData.length === 0) {
            return { total_surcharge: 0, total_amount: 0 };
        }
        return {
            total_surcharge: mappedData.reduce((acc, item) => acc + item.surcharge, 0),
            total_amount: mappedData.reduce((acc, item) => acc + item.amount, 0),
        };
    }, [mappedData]); // Dependensi: hanya kalkulasi ulang jika mappedData berubah

    const onSubmit = useCallback((data: FilterSchemaType) => {
        if (!data.filter.periode.start || !data.filter.periode.end) {
            toast.error("Please select property and a valid date range.");
            return;
        }

        if (showRefresh) {
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string' && key.startsWith('payment-report');
                }
            });
        }

        setActiveFilter(data.filter);
    }, [showRefresh, queryClient]);

    // Toast notifikasi saat data berhasil di-load
    useEffect(() => {
        if (!isFetching && reportData) {
            toast.success('Payment report data loaded successfully.');
        }
    }, [isFetching, reportData]);

    const renderReportContent = () => {
        if (!activeFilter) {
            return <Alert variant="info" className="mt-4 text-center">Please select your filters to view the report.</Alert>;
        }
        if (isFetching) {
            return <SkeletonTable columns={12} rows={10} />;
        }
        if (isError) {
            return <Alert variant="danger" className="mt-4"><strong>Error:</strong> Failed to load payment report. {error instanceof Error ? error.message : String(error)}</Alert>;
        }
        // 4. Gunakan `mappedData` dan `totals` yang sudah diproses untuk dirender
        if (reportData && mappedData.length > 0) {
            return (
                <PaymentTableReport
                    data={{
                        data: mappedData, // <-- Kirim data yang sudah di-map
                        success: reportData.success,
                        message: reportData.message,
                        code: reportData.code,
                        total: totals, // <-- Kirim total yang sudah dihitung
                    }}
                    filter={activeFilter} // <-- Pass the filter data to child component
                />
            );
        }
        if (reportData && mappedData.length === 0) {
            return <Alert variant="secondary" className="mt-4 text-center">No payment data found for the selected criteria.</Alert>;
        }
        return null;
    };

    return (
        <Container fluid className="dashboard-content">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="d-flex flex-column flex-md-row justify-content-between gap-4">
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="w-100"><PropertySelectComponent setValue={setValue} /></div>
                        <div className="w-100"><RoomSelectComponent control={control} errors={errors} setValue={setValue} /></div>
                    </div>
                    <div className="d-flex flex-column flex-md-row gap-3 w-100 align-items-end">
                        <div className="w-100"><DateRangePickerComponent control={control} errors={errors} setValue={setValue} /></div>
                        <div className="w-100 w-md-auto">
                            <Button 
                                variant={showRefresh ? "info" : "warning"} 
                                type="submit" 
                                className="w-100" 
                                disabled={isFetching}
                            >
                                {isFetching ? (
                                    <><Spinner as="span" animation="border" size="sm" /> Loading...</>
                                ) : showRefresh ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Payment Report</>
                                ) : (
                                    'Show Payment Detail'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
            <div className="mt-4">{renderReportContent()}</div>
        </Container>
    );
};

export default PaymentReportComponent;